import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Edit, X, ChevronUp, ChevronDown, User, Mail, Phone, Calendar, Globe, MapPin, Users, Search, Filter, ArrowUpDown, Smartphone, Eye, Building2 } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";

const Members = () => {
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    alternate_phone: "",
    company_name: "",
    employee_count: "",
    website: "",
    address: "",
    password: "",
    confirm_password: ""
  });

  const modalRef = useRef(null);
  const viewModalRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Responsive check
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Fetch members
  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://backend.dlapakistan.com/api/members/');

      let membersData = [];
      if (response.data.success && response.data.members) {
        membersData = response.data.members;
      } else if (Array.isArray(response.data)) {
        membersData = response.data;
      } else if (response.data.members) {
        membersData = response.data.members;
      } else {
        membersData = Array.isArray(response.data) ? response.data : [];
      }

      membersData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setMembers(membersData);

    } catch (error) {
      console.error("Failed to fetch members:", error.response?.data || error);
      Swal.fire({
        icon: "error",
        title: "Failed to load members",
        text: error.response?.data?.detail || "Please try again",
        timer: 3000,
      });
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  // Filter members based on search
  const filteredMembers = members.filter(member => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      member.first_name?.toLowerCase().includes(searchLower) ||
      member.last_name?.toLowerCase().includes(searchLower) ||
      member.email?.toLowerCase().includes(searchLower) ||
      member.company_name?.toLowerCase().includes(searchLower) ||
      member.phone_number?.includes(searchTerm) ||
      member.alternate_phone?.includes(searchTerm) ||
      member.address?.toLowerCase().includes(searchLower) ||
      member.website?.toLowerCase().includes(searchLower) ||
      member.employee_count?.toLowerCase().includes(searchLower)
    );
  });

  // Sort members
  const sortMembers = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedMembers = [...filteredMembers].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setMembers(sortedMembers);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 sm:w-4 sm:h-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" /> :
      <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />;
  };

  // Click outside modal for edit/add modal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
      }
    };

    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  // Click outside modal for view modal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (viewModalRef.current && !viewModalRef.current.contains(e.target)) {
        closeViewModal();
      }
    };

    if (showViewModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [showViewModal]);

  const closeModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setForm({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      alternate_phone: "",
      company_name: "",
      employee_count: "",
      website: "",
      address: "",
      password: "",
      confirm_password: ""
    });
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingMember(null);
  };

  const handleView = (member) => {
    setViewingMember(member);
    setShowViewModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.first_name || !form.last_name || !form.email || !form.phone_number) {
      Swal.fire("Missing Fields", "Please fill all required fields", "error");
      return;
    }

    if (!editingMember && (!form.password || !form.confirm_password)) {
      Swal.fire("Password Required", "Please enter and confirm password for new member", "error");
      return;
    }

    if (form.password && form.password !== form.confirm_password) {
      Swal.fire("Passwords Mismatch", "Passwords do not match", "error");
      return;
    }

    if (form.password && form.password.length < 6) {
      Swal.fire("Password Too Short", "Password must be at least 6 characters", "error");
      return;
    }

    try {
      const memberData = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone_number: form.phone_number,
        alternate_phone: form.alternate_phone || "",
        company_name: form.company_name || "",
        employee_count: form.employee_count || "",
        website: form.website || "",
        address: form.address || ""
      };

      if (form.password) {
        memberData.password = form.password;
        memberData.confirm_password = form.confirm_password;
      }

      if (editingMember) {
        await axios.put(`https://backend.dlapakistan.com/api/members/${editingMember.id}/`, memberData);
        Swal.fire("Success", "Member updated successfully", "success");
      } else {
        await axios.post('https://backend.dlapakistan.com/api/members/', memberData);
        Swal.fire("Success", "Member added successfully", "success");
      }

      await fetchMembers();
      closeModal();
    } catch (error) {
      console.error("Error:", error.response?.data || error);

      let errorMessage = "Something went wrong";

      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          const errors = [];
          for (const key in error.response.data) {
            if (Array.isArray(error.response.data[key])) {
              errors.push(...error.response.data[key]);
            } else {
              errors.push(error.response.data[key]);
            }
          }
          if (errors.length > 0) {
            errorMessage = errors.join(', ');
          }
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        timer: 3000,
      });
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: "Delete member?",
      text: `Delete ${name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Delete",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`https://backend.dlapakistan.com/api/members/${id}/`);
        setMembers(prev => prev.filter(m => m.id !== id));
        Swal.fire("Deleted!", "Member removed", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete", "error");
      }
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setForm({
      first_name: member.first_name,
      last_name: member.last_name,
      email: member.email,
      phone_number: member.phone_number,
      alternate_phone: member.alternate_phone || "",
      company_name: member.company_name || "",
      employee_count: member.employee_count || "",
      website: member.website || "",
      address: member.address || "",
      password: "",
      confirm_password: ""
    });
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatWebsite = (url) => {
    if (!url) return 'N/A';
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  // Mobile Card View
  const renderMobileCard = (member) => (
    <div key={member.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300 w-full overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0 h-14 w-14 bg-gradient-to-br from-emerald-500 to-green-500 rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-xl font-bold text-white">
              {member.first_name?.[0]}{member.last_name?.[0]}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-gray-900 truncate">
              {member.first_name} {member.last_name}
            </h3>
            {member.company_name && (
              <p className="text-sm text-emerald-600 font-medium truncate">
                <Building2 className="w-3 h-3 inline mr-1" />
                {member.company_name}
              </p>
            )}
            <p className="text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full inline-block mt-1 truncate max-w-full">
              ID: {member.id?.substring(0, 8)}...
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => handleView(member)}
            className="p-2.5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 text-blue-600 rounded-xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-200"
            title="View member details"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEdit(member)}
            className="p-2.5 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 text-emerald-600 rounded-xl hover:from-emerald-100 hover:to-green-100 transition-all duration-200"
            title="Edit member"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(member.id, `${member.first_name} ${member.last_name}`)}
            className="p-2.5 bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 text-red-600 rounded-xl hover:from-red-100 hover:to-rose-100 transition-all duration-200"
            title="Delete member"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Details Grid */}
      <div className="space-y-2 w-full">
        {/* Email */}
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl w-full overflow-hidden">
          <Mail className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <a
            href={`mailto:${member.email}`}
            className="text-sm text-gray-700 hover:text-emerald-600 break-all break-words hyphens-auto"
            style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: 'calc(100% - 2rem)' }}
          >
            {member.email}
          </a>
        </div>

        {/* Primary Phone */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl w-full">
          <Phone className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <span className="text-sm text-gray-700 break-all">{member.phone_number}</span>
        </div>

        {/* Alternate Phone */}
        {member.alternate_phone && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl w-full">
            <Smartphone className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span className="text-sm text-gray-700 break-all">{member.alternate_phone}</span>
          </div>
        )}

        {/* Employee Count */}
        {member.employee_count && (
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl w-full">
            <Users className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700 break-words">{member.employee_count}</span>
          </div>
        )}

        {/* Website */}
        {member.website && (
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl w-full overflow-hidden">
            <Globe className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <a
              href={member.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-emerald-600 hover:text-emerald-700 break-all break-words hyphens-auto"
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: 'calc(100% - 2rem)' }}
            >
              {member.website}
            </a>
          </div>
        )}

        {/* Address */}
        {member.address && (
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl w-full">
            <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <span
              className="text-sm text-gray-700 whitespace-pre-wrap break-words"
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: 'calc(100% - 2rem)' }}
            >
              {member.address}
            </span>
          </div>
        )}

        {/* Date */}
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl w-full">
          <Calendar className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 truncate">
            Joined: {formatDate(member.created_at)}
          </span>
        </div>
      </div>
    </div>
  );

  // Desktop Table View - WITH FIXED HEADER AND SCROLLABLE BODY
  const renderDesktopRow = (member) => (
    <tr
      key={member.id}
      className="hover:bg-emerald-50/30 transition-colors duration-200 group"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-all">
            <span className="text-lg font-bold text-white">
              {member.first_name?.[0]}{member.last_name?.[0]}
            </span>
          </div>
          <div className="ml-4 min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">
              {member.first_name} {member.last_name}
            </div>
            <div className="text-xs text-emerald-600 font-medium truncate max-w-[150px]">
              {member.id?.substring(0, 8)}...
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]" title={member.email}>
            {member.email}
          </div>
          <div className="text-xs text-gray-500 mt-1 truncate max-w-[150px]" title={member.phone_number}>
            📱 Primary: {member.phone_number}
          </div>
          {member.alternate_phone && (
            <div className="text-xs text-gray-500 mt-1 truncate max-w-[150px]" title={member.alternate_phone}>
              📞 Alt: {member.alternate_phone}
            </div>
          )}
        </div>
      </td>
      {/* COMPANY/LAB NAME COLUMN */}
      <td className="px-6 py-4">
        {member.company_name ? (
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <Building2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span
                className="text-sm text-gray-700 block truncate max-w-[150px]"
                title={member.company_name}
              >
                {member.company_name}
              </span>
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-400 flex items-center gap-1">
            <Building2 className="w-4 h-4 text-gray-300" />
            —
          </span>
        )}
      </td>
      <td className="px-6 py-4">
        {member.employee_count ? (
          <div className="min-w-0">
            <span className="text-sm text-gray-700 block truncate max-w-[150px]" title={member.employee_count}>
              {member.employee_count}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
      <td className="px-6 py-4">
        {member.website ? (
          <div className="min-w-0">
            <a
              href={member.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline block truncate max-w-[150px]"
              title={member.website}
            >
              {formatWebsite(member.website)}
            </a>
          </div>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
      <td className="px-6 py-4">
        {member.address ? (
          <div className="min-w-0">
            <span
              className="text-sm text-gray-700 block truncate max-w-[150px]"
              title={member.address}
            >
              {member.address}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-700 bg-gray-50 px-3 py-1.5 rounded-full">
          {formatDate(member.created_at)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleView(member)}
            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            title="View member details"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEdit(member)}
            className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors"
            title="Edit member"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(member.id, `${member.first_name} ${member.last_name}`)}
            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            title="Delete member"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  // Global Styles
  const globalStyles = `
    .break-all {
      word-break: break-all !important;
      overflow-wrap: break-word !important;
      word-wrap: break-word !important;
      hyphens: auto !important;
    }
    
    /* Custom scrollbar styles */
    .custom-scrollbar::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #10b981;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #059669;
    }
    
    /* For Firefox */
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: #10b981 #f1f1f1;
    }
    
    /* Table container with fixed header */
    .table-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      border-radius: 1rem;
      overflow: hidden;
      background: white;
    }
    
    /* Fixed header */
    .table-header {
      flex-shrink: 0;
      background: linear-gradient(to right, #f9fafb, #f0fdf4);
      border-bottom: 1px solid #e5e7eb;
    }
    
    /* Scrollable body */
    .table-body {
      flex: 1;
      overflow-y: auto;
      overflow-x: auto;
      max-height: calc(100vh - 350px);
      scrollbar-width: thin;
      scrollbar-color: #10b981 #f1f1f1;
    }
    
    .table-body::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    .table-body::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    
    .table-body::-webkit-scrollbar-thumb {
      background: #10b981;
      border-radius: 10px;
    }
    
    .table-body::-webkit-scrollbar-thumb:hover {
      background: #059669;
    }
    
    /* Table styles */
    .fixed-table {
      min-width: 100%;
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
    }
    
    .fixed-table th {
      position: sticky;
      top: 0;
      background: inherit;
      z-index: 10;
    }
    
    td, th {
      max-width: 0;
      overflow: hidden;
    }
    
    .line-clamp-2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    
    .hover\\:line-clamp-none:hover {
      -webkit-line-clamp: unset;
    }
    
    input, textarea {
      text-overflow: ellipsis;
    }
    
    .modal-content {
      word-break: break-word;
      overflow-wrap: break-word;
    }
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50/30">
      <style>{globalStyles}</style>

      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-emerald-700 to-green-700 bg-clip-text text-transparent">
                Members
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                Manage your member accounts and information
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Bar */}
              <div className="relative flex-grow sm:min-w-[300px]">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl bg-white/50 backdrop-blur-sm focus:ring-2
                  focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-sm sm:text-base"
                />
              </div>

              <button
                onClick={() => setShowModal(true)}
                className="flex items-center justify-center px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-600 to-green-600
                hover:from-emerald-700 hover:to-green-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
              >
                <Plus className="w-5 h-5 mr-2" />
                <span>Add Member</span>
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2 bg-emerald-50 px-4 py-2 rounded-full">
              <Users className="w-4 h-4 text-emerald-600" />
              <span className="font-medium text-emerald-700">
                {filteredMembers.length} Members
              </span>
            </div>
            {searchTerm && (
              <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                <Filter className="w-4 h-4 text-gray-600" />
                <span className="text-gray-700">
                  Filtered: {filteredMembers.length} results
                </span>
                <button
                  onClick={() => setSearchTerm("")}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 sm:py-24 lg:py-32">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 sm:h-20 sm:w-20 border-4 border-emerald-200 border-t-emerald-600"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Users className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-600 mt-6 text-base sm:text-lg font-medium">
              Loading members...
            </p>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-16 sm:py-20 lg:py-24">
            <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-6 sm:mb-8 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
              {searchTerm ? (
                <Search className="w-12 h-12 sm:w-16 sm:h-16 text-emerald-400" />
              ) : (
                <Users className="w-12 h-12 sm:w-16 sm:h-16 text-emerald-400" />
              )}
            </div>
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-3">
              {searchTerm ? "No members found" : "No members yet"}
            </h3>
            <p className="text-gray-600 text-base sm:text-lg mb-8 break-all max-w-2xl mx-auto px-4">
              {searchTerm
                ? `No results matching "${searchTerm}"`
                : "Get started by adding your first member"}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowModal(true)}
                className="px-8 py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:from-emerald-700 hover:to-green-700 font-medium text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Create First Member
              </button>
            )}
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="px-8 py-4 bg-gray-600 text-white rounded-xl hover:bg-gray-700 font-medium text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile View ( < 640px ) */}
            {isMobile && (
              <div className="space-y-4 w-full">
                <div className="grid grid-cols-1 gap-4 w-full">
                  {filteredMembers.map(renderMobileCard)}
                </div>
              </div>
            )}

            {/* Tablet View ( 640px - 1024px ) */}
            {isTablet && (
              <div className="space-y-6 w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                  {filteredMembers.map(renderMobileCard)}
                </div>
              </div>
            )}

            {/* Desktop View ( > 1024px ) - WITH FIXED HEADER AND SCROLLABLE BODY */}
            {!isMobile && !isTablet && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden w-full">
                <div className="table-container">
                  {/* Table Header - FIXED */}
                  <div className="table-header">
                    <table className="fixed-table">
                      <colgroup>
                        <col className="w-[15%]" />
                        <col className="w-[18%]" />
                        <col className="w-[12%]" />
                        <col className="w-[12%]" />
                        <col className="w-[10%]" />
                        <col className="w-[15%]" />
                        <col className="w-[10%]" />
                        <col className="w-[8%]" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col" className="px-6 py-4 text-left">
                            <button
                              onClick={() => sortMembers('first_name')}
                              className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-emerald-600"
                            >
                              <User className="w-4 h-4 text-emerald-600" />
                              Member
                              {getSortIcon('first_name')}
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left">
                            <button
                              onClick={() => sortMembers('email')}
                              className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-emerald-600"
                            >
                              <Mail className="w-4 h-4 text-emerald-600" />
                              Contact
                              {getSortIcon('email')}
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left">
                            <button
                              onClick={() => sortMembers('company_name')}
                              className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-emerald-600"
                            >
                              <Building2 className="w-4 h-4 text-emerald-600" />
                              Company/Lab
                              {getSortIcon('company_name')}
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left">
                            <button
                              onClick={() => sortMembers('employee_count')}
                              className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-emerald-600"
                            >
                              <Users className="w-4 h-4 text-emerald-600" />
                              Employees
                              {getSortIcon('employee_count')}
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left">
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              <Globe className="w-4 h-4 text-emerald-600" />
                              Website
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left">
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              <MapPin className="w-4 h-4 text-emerald-600" />
                              Address
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left">
                            <button
                              onClick={() => sortMembers('created_at')}
                              className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-emerald-600"
                            >
                              <Calendar className="w-4 h-4 text-emerald-600" />
                              Joined
                              {getSortIcon('created_at')}
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left">
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Actions
                            </span>
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>

                  {/* Table Body - SCROLLABLE */}
                  <div
                    ref={tableBodyRef}
                    className="table-body"
                  >
                    <table className="fixed-table">
                      <colgroup>
                        <col className="w-[15%]" />
                        <col className="w-[18%]" />
                        <col className="w-[12%]" />
                        <col className="w-[12%]" />
                        <col className="w-[10%]" />
                        <col className="w-[15%]" />
                        <col className="w-[10%]" />
                        <col className="w-[8%]" />
                      </colgroup>
                      <tbody>
                        {filteredMembers.map(renderDesktopRow)}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Desktop Footer */}
                <div className="bg-gradient-to-r from-gray-50 to-emerald-50/50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-semibold text-emerald-700">{filteredMembers.length}</span> of{' '}
                      <span className="font-semibold text-gray-900">{members.length}</span> members
                    </div>
                    <div className="text-xs text-gray-500">
                      Last updated: {new Date().toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit/Add Modal */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] transition-opacity duration-300"
            onClick={closeModal}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 z-[10000] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-6 sm:p-6">
              {/* Modal Content */}
              <div
                ref={modalRef}
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto border border-emerald-100"
                style={{
                  maxHeight: 'calc(100vh - 40px)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-green-50 rounded-t-2xl">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-white rounded-xl shadow-sm flex-shrink-0">
                      {editingMember ? (
                        <Edit className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <Plus className="w-5 h-5 text-emerald-600" />
                      )}
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 truncate">
                      {editingMember ? "Edit Member" : "Add New Member"}
                    </h2>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-white/50 rounded-full transition-all duration-200 hover:rotate-90 flex-shrink-0"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto flex-grow custom-scrollbar">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-2">
                        Personal Information
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="first_name"
                            value={form.first_name}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                            placeholder="John"
                            required
                          />
                        </div>

                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="last_name"
                            value={form.last_name}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                            placeholder="Doe"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500
                        focus-within:border-emerald-500 transition-all">
                          <div className="px-3 py-2.5 bg-gray-50 rounded-l-xl border-r border-gray-300 flex-shrink-0">
                            <Mail className="w-5 h-5 text-gray-500" />
                          </div>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-r-xl focus:outline-none min-w-0"
                            placeholder="john@example.com"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Phone Numbers Section */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-2">
                        Phone Numbers
                      </h3>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                          Primary Phone <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500
                        focus-within:border-emerald-500 transition-all">
                          <div className="px-3 py-2.5 bg-gray-50 rounded-l-xl border-r border-gray-300 flex-shrink-0">
                            <Phone className="w-5 h-5 text-gray-500" />
                          </div>
                          <input
                            type="tel"
                            name="phone_number"
                            value={form.phone_number}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-r-xl focus:outline-none min-w-0"
                            placeholder="+1 (555) 000-0000"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                          Alternate Phone
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500
                        focus-within:border-emerald-500 transition-all">
                          <div className="px-3 py-2.5 bg-gray-50 rounded-l-xl border-r border-gray-300 flex-shrink-0">
                            <Smartphone className="w-5 h-5 text-gray-500" />
                          </div>
                          <input
                            type="tel"
                            name="alternate_phone"
                            value={form.alternate_phone}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-r-xl focus:outline-none min-w-0"
                            placeholder="+1 (555) 111-1111"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Company Information Section */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-2">
                        Company Information
                      </h3>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                          <Building2 className="w-4 h-4 inline mr-1 text-emerald-600" />
                          Company / Lab Name
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500
                        focus-within:border-emerald-500 transition-all">
                          <div className="px-3 py-2.5 bg-gray-50 rounded-l-xl border-r border-gray-300 flex-shrink-0">
                            <Building2 className="w-5 h-5 text-gray-500" />
                          </div>
                          <input
                            type="text"
                            name="company_name"
                            value={form.company_name}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-r-xl focus:outline-none min-w-0"
                            placeholder="e.g., Acme Corporation, BioLab Inc."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Information */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-2">
                        Business Information
                      </h3>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                          <Users className="w-4 h-4 inline mr-1 text-emerald-600" />
                          Employee Count / Details
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500
                        focus-within:border-emerald-500 transition-all">
                          <div className="px-3 py-2.5 bg-gray-50 rounded-l-xl border-r border-gray-300 flex-shrink-0">
                            <Users className="w-5 h-5 text-gray-500" />
                          </div>
                          <textarea
                            name="employee_count"
                            value={form.employee_count}
                            onChange={handleChange}
                            rows="2"
                            className="w-full px-4 py-2.5 rounded-r-xl focus:outline-none min-w-0 resize-none"
                            placeholder="e.g., 50 full-time, 10 part-time, or any description"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">You can enter numbers or text description</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                          <Globe className="w-4 h-4 inline mr-1 text-emerald-600" />
                          Website URL
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500
                         focus-within:border-emerald-500 transition-all">
                          <div className="px-3 py-2.5 bg-gray-50 rounded-l-xl border-r border-gray-300 flex-shrink-0">
                            <Globe className="w-5 h-5 text-gray-500" />
                          </div>
                          <input
                            type="url"
                            name="website"
                            value={form.website}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-r-xl focus:outline-none min-w-0"
                            placeholder="https://example.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                          <MapPin className="w-4 h-4 inline mr-1 text-emerald-600" />
                          Address
                        </label>
                        <div className="flex border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500
                        focus-within:border-emerald-500 transition-all overflow-hidden">
                          <div className="px-3 py-2.5 bg-gray-50 border-r border-gray-300 flex-shrink-0">
                            <MapPin className="w-5 h-5 text-gray-500" />
                          </div>
                          <textarea
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            rows="3"
                            className="w-full px-4 py-2.5 focus:outline-none resize-none min-w-0 break-all"
                            placeholder="Street address, city, state, zip code"
                            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Security */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-2">
                        Security
                      </h3>

                      {!editingMember ? (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="min-w-0">
                              <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                                Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                name="password"
                                value={form.password}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                                placeholder="••••••••"
                                required
                              />
                            </div>
                            <div className="min-w-0">
                              <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                                Confirm Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                name="confirm_password"
                                value={form.confirm_password}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                                placeholder="••••••••"
                                required
                              />
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 break-all">Minimum 6 characters</p>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                              New Password
                            </label>
                            <input
                              type="password"
                              name="password"
                              value={form.password}
                              onChange={handleChange}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                              placeholder="Leave blank to keep current"
                            />
                          </div>
                          {form.password && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
                                Confirm New Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                name="confirm_password"
                                value={form.confirm_password}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                                placeholder="Confirm new password"
                                required
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </form>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-5 py-2.5 border-2 border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-all duration-200 whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-medium hover:from-emerald-700
                     hover:to-green-700 shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
                  >
                    {editingMember ? 'Update Member' : 'Add Member'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* View Modal */}
      {showViewModal && viewingMember && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] transition-opacity duration-300"
            onClick={closeViewModal}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 z-[10000] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-6 sm:p-6">
              {/* Modal Content */}
              <div
                ref={viewModalRef}
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto border border-emerald-100"
                style={{
                  maxHeight: 'calc(100vh - 40px)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-emerald-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-white rounded-xl shadow-sm flex-shrink-0">
                      <Eye className="w-5 h-5 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 truncate">
                      Member Details
                    </h2>
                  </div>
                  <button
                    onClick={closeViewModal}
                    className="p-2 hover:bg-white/50 rounded-full transition-all duration-200 hover:rotate-90 flex-shrink-0"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Modal Body - View Only with Scrollbar */}
                <div className="p-6 overflow-y-auto flex-grow custom-scrollbar">
                  <div className="space-y-6">
                    {/* Profile Header */}
                    <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
                      <div className="flex-shrink-0 h-20 w-20 bg-gradient-to-br from-emerald-500 to-green-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <span className="text-3xl font-bold text-white">
                          {viewingMember.first_name?.[0]}{viewingMember.last_name?.[0]}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 truncate">
                          {viewingMember.first_name} {viewingMember.last_name}
                        </h3>
                        {viewingMember.company_name && (
                          <p className="text-sm text-emerald-600 font-medium mt-1">
                            <Building2 className="w-4 h-4 inline mr-1" />
                            {viewingMember.company_name}
                          </p>
                        )}
                        <p className="text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full inline-block mt-1">
                          ID: {viewingMember.id}
                        </p>
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Email */}
                      <div className="col-span-1 md:col-span-2 bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email Address</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Mail className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <a href={`mailto:${viewingMember.email}`} className="text-gray-900 hover:text-emerald-600 break-all">
                            {viewingMember.email}
                          </a>
                        </div>
                      </div>

                      {/* Primary Phone */}
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Primary Phone</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Phone className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <span className="text-gray-900">{viewingMember.phone_number}</span>
                        </div>
                      </div>

                      {/* Alternate Phone */}
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Alternate Phone</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Smartphone className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <span className="text-gray-900">{viewingMember.alternate_phone || '—'}</span>
                        </div>
                      </div>

                      {/* Employee Count */}
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee Details</label>
                        <div className="flex items-start gap-2 mt-1">
                          <Users className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-900 whitespace-pre-wrap break-words">{viewingMember.employee_count || '—'}</span>
                        </div>
                      </div>

                      {/* Website */}
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Website</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Globe className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          {viewingMember.website ? (
                            <a href={viewingMember.website} target="_blank" rel="noopener noreferrer" className="text-emerald-600
                             hover:text-emerald-700 hover:underline break-all">
                              {viewingMember.website}
                            </a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </div>

                      {/* Address - Full width */}
                      <div className="col-span-1 md:col-span-2 bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</label>
                        <div className="flex items-start gap-2 mt-1">
                          <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-900 whitespace-pre-wrap break-words">{viewingMember.address || '—'}</span>
                        </div>
                      </div>

                      {/* Created At */}
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Member Since</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <span className="text-gray-900">{formatDateTime(viewingMember.created_at)}</span>
                        </div>
                      </div>

                      {/* Last Updated */}
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Updated</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <span className="text-gray-900">{formatDateTime(viewingMember.updated_at) || '—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={closeViewModal}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl
                    font-medium hover:from-emerald-700 hover:to-green-700 shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Members;